'use client';

import { type ComponentType, type ReactNode, useMemo } from 'react';

import { ChevronRight, Lock, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';
import { LocationFunnelPicker } from '~/(tenant)/book/_components/location-funnel-picker';
import type { PublicLocationRow } from '~/(tenant)/book/_lib/server/booking-page.loader';

import { type VenueOption, useVenueLabel } from './use-venue-picker';

type SlotComponent = ComponentType<{ asChild?: boolean; children: ReactNode; className?: string }>;

interface VenuePickerListProps {
  venues: VenueOption[];
  activeVenue: VenueOption | null;
  needsInitialPick: boolean;
  onPick: (venue: VenueOption) => void;
  /** Rich location rows for school networks: the venues are then chosen
   *  through the same Region → City → Gender → Club funnel /book uses. */
  funnelLocations?: PublicLocationRow[];
  /** DrawerTitle or DialogTitle, so the heading labels whichever surface hosts the list. */
  Title: SlotComponent;
  Description: SlotComponent;
}

/**
 * Heading + venue rows shared by the native bottom sheet and the web picker,
 * styled with the tenant's native tokens so both surfaces look the same.
 */
export function VenuePickerList({
  venues,
  activeVenue,
  needsInitialPick,
  onPick,
  funnelLocations,
  Title,
  Description,
}: VenuePickerListProps) {
  const t = useNativeTokens();
  const { t: tr, i18n } = useTranslation('bookings');
  const labelFor = useVenueLabel();

  return (
    <>
      <Title asChild>
        <div style={{ marginBottom: 6 }}>
          <div
            style={{
              fontFamily: t.fontMono,
              fontSize: 9,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: t.muted,
              marginBottom: 4,
            }}
          >
            {needsInitialPick
              ? tr('venueSwitcher.welcomeEyebrow', { defaultValue: 'Welcome' })
              : tr('venueSwitcher.eyebrow', { defaultValue: 'Venue' })}
          </div>
          <h2
            style={{
              fontFamily: t.fontDisplay,
              fontSize: 22,
              fontWeight: 500,
              margin: 0,
              letterSpacing: '-0.01em',
              color: t.foreground,
            }}
          >
            {needsInitialPick
              ? tr('venueSwitcher.pickYourVenue', {
                  defaultValue: 'Pick your venue to get started',
                })
              : tr('venueSwitcher.chooseVenue', {
                  defaultValue: 'Choose a venue',
                })}
          </h2>
        </div>
      </Title>
      <Description className="sr-only">
        {tr('venueSwitcher.description', {
          defaultValue: 'Switch the active venue for your bookings.',
        })}
      </Description>

      <div style={{ marginTop: 18 }}>
        {funnelLocations ? (
          <VenueFunnel
            venues={venues}
            activeVenue={activeVenue}
            onPick={onPick}
            locations={funnelLocations}
            preferAlt={i18n.language === 'ar'}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {venues.map((venue) => (
              <VenueRow
                key={venue.id}
                venue={venue}
                label={labelFor(venue)}
                isActive={venue.slug === activeVenue?.slug}
                onPick={onPick}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function VenueFunnel({
  venues,
  activeVenue,
  onPick,
  locations,
  preferAlt,
}: {
  venues: VenueOption[];
  activeVenue: VenueOption | null;
  onPick: (venue: VenueOption) => void;
  locations: PublicLocationRow[];
  preferAlt: boolean;
}) {
  const imagesByLocation = useMemo(
    () =>
      Object.fromEntries(
        locations
          .filter((location) => location.image_urls?.length)
          .map((location) => [location.id, location.image_urls ?? []]),
      ),
    [locations],
  );

  return (
    <LocationFunnelPicker
      locations={locations}
      selectedLocationId={activeVenue?.id ?? null}
      onLocationSelect={(locationId) => {
        const venue = venues.find((option) => option.id === locationId);
        if (venue) onPick(venue);
      }}
      imagesByLocation={imagesByLocation}
      preferAlt={preferAlt}
      collapseOnSelect={false}
    />
  );
}

function VenueRow({
  venue,
  label,
  isActive,
  onPick,
}: {
  venue: VenueOption;
  label: { name: string; city: string | null };
  isActive: boolean;
  onPick: (venue: VenueOption) => void;
}) {
  const t = useNativeTokens();
  const { t: tr } = useTranslation('bookings');
  const isCodeOnly = venue.accessMode === 'code_only';

  return (
    <button
      data-native-selection-row
      data-test="venue-picker-option"
      data-test-venue-slug={venue.slug}
      type="button"
      aria-pressed={isActive}
      onClick={() => onPick(venue)}
      className="transition-opacity hover:opacity-90 active:opacity-70"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        width: '100%',
        padding: '16px 16px',
        background: t.surface,
        border: `1.5px solid ${isActive ? t.accent : t.border}`,
        borderRadius: t.radius,
        cursor: 'pointer',
        textAlign: 'start',
        color: t.foreground,
      }}
    >
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 36,
          height: 36,
          borderRadius: t.radius,
          background: isActive ? t.accent : t.line,
          color: isActive ? t.background : t.foreground,
          flexShrink: 0,
        }}
      >
        {isCodeOnly ? (
          <Lock size={17} strokeWidth={1.5} />
        ) : (
          <MapPin size={17} strokeWidth={1.5} />
        )}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: t.fontDisplay,
            fontSize: 17,
            fontWeight: isActive ? 600 : 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label.name}
        </div>
        {label.city && (
          <div
            style={{
              fontFamily: t.fontMono,
              fontSize: 9,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: t.muted,
              marginTop: 4,
            }}
          >
            {label.city}
          </div>
        )}
      </div>
      {isActive ? (
        <span
          style={{
            fontFamily: t.fontMono,
            fontSize: 9,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: t.accent,
            flexShrink: 0,
          }}
        >
          {tr('venueSwitcher.current', { defaultValue: 'Current' })}
        </span>
      ) : (
        <ChevronRight
          size={18}
          strokeWidth={1.5}
          className="rtl:rotate-180"
          style={{ color: t.muted, flexShrink: 0 }}
        />
      )}
    </button>
  );
}
